import React, { useState, useEffect, FC, Fragment } from "react";
import { ReactComponent as ExitIcon } from "@images/exit_icon.svg";
import { useHotkeys } from "react-hotkeys-hook";
import IconButton from "@components/common/IconButton";
import { useAuth } from "@providers/AuthContext";
import { NLMail, NLMailboxProfileType } from "@models/Models";
import Alert, { AlertState } from "@components/common/Alert";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { getMailboxAliases } from "@utils/managers/networking/NetworkManager";
import { createMailboxAlias } from "@utils/managers/networking/NetworkManager";
import { deleteMailboxAlias } from "@utils/managers/networking/NetworkManager";
import { ReactComponent as DeleteIcon } from "@images/delete_icon.svg";


interface MailboxAliasModalProps {
    mailboxProfileId: string | undefined;
	isShow: boolean;
	onClose: () => void;
}

const MailboxAliasModal: FC<MailboxAliasModalProps> = ({ mailboxProfileId, isShow, onClose }) => {
	const { authUser } = useAuth();
	const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});
	const navigate = useNavigate();
    const [aliases, setAliases] = useState<string[]>([]);
	const [isAdding, setIsAdding] = useState<boolean>(false);
	const [newAliasEmail, setNewAliasEmail] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	useHotkeys("Escape", () => { if (!alertState.isShow) { onModalClose(); } });

	const onModalClose = () => {
		onClose();
	};

	useEffect(() => {
        
        if (isShow && mailboxProfileId) {
            //Get aliases for the mailbox
            getMailboxAliases(mailboxProfileId).then((aliases) => {
                setAliases(aliases);
            });
        }

    }, [isShow, mailboxProfileId]);

	const onAddAlias = () => {
		if (!mailboxProfileId || !newAliasEmail || isSubmitting) {
			return;
		}
		setIsSubmitting(true);
		createMailboxAlias(mailboxProfileId, newAliasEmail.trim())
			.then(() => {
				return getMailboxAliases(mailboxProfileId);
			})
			.then((aliases) => {
				setAliases(aliases);
				setNewAliasEmail("");
				setIsAdding(false);
			})
			.catch((_error) => {
				setAlertState({ isShow: true, title: "Couldn't add alias", message: "Please try again.", actionButton: "OK", dismissButton: null, actionButtonAction: undefined });
			})
			.finally(() => setIsSubmitting(false));
	};

	const isEmailValid = (email: string) => {
		// basic check; HTML5 validation will also help
		return email.includes("@") && email.includes(".");
	};

	const confirmDeleteAlias = (aliasEmail: string) => {
		setAlertState({
			isShow: true,
			title: "Delete alias?",
			message: "Are you sure you want to delete this alias?",
			actionButton: "Delete",
			dismissButton: "Cancel",
			actionButtonAction: () => handleDeleteAlias(aliasEmail),
		});
	};

	const handleDeleteAlias = (aliasEmail: string) => {
		if (!mailboxProfileId) {
			return;
		}
		deleteMailboxAlias(mailboxProfileId, aliasEmail)
			.then(() => getMailboxAliases(mailboxProfileId))
			.then((aliases) => {
				setAliases(aliases);
				setAlertState({ isShow: false, title: null, message: null, actionButton: null, dismissButton: null, actionButtonAction: undefined });
			})
			.catch((_error) => {
				setAlertState({ isShow: true, title: "Couldn't delete alias", message: "Please try again.", actionButton: "OK", dismissButton: null, actionButtonAction: undefined });
			});
	};

	return (
		<>
			<Alert alertState={alertState} setAlertState={setAlertState} />

			
			<Transition as={Fragment} show={isShow}>
				<Dialog onClose={() => { if (!alertState.isShow) { onClose(); } }} className="relative z-40">
					<Transition.Child as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
						<div className="fixed inset-0 bg-black bg-opacity-70" aria-hidden="true" />
					</Transition.Child>
					<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
						<Transition.Child className="w-full h-full" enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
							<Dialog.Panel className="m-auto w-full h-full max-w-lg transform overflow-hidden rounded-2xl bg-secondary border border-primary-200 text-left align-middle shadow-xl transition-all">
								<div className="absolute w-full bg-surface backdrop-blur-md top-0 left-0 flex flex-row justify-between items-center p-4 pb-3 border-b border-primary-200 z-20">
									<div className="text-primary font-medium font-primary text-xl">Aliases</div>
									<IconButton Icon={ExitIcon} className="-mr-2.5" onClick={() => onModalClose()} />
								</div>
								<div className="flex flex-col gap-4 text-left h-full p-4 py-[105px] bg-secondary overflow-auto">
									<div className="w-full p-4 bg-surface border border-primary-100 rounded-xl text-center flex flex-col gap-3">
										<span className="font-primary font-regular text-sm text-primary-500">If you plan to use a service like SimpleLogin to automatically forward emails to your Meco Inbox, please add the forwarding email address below.</span>
										{!isAdding && (
											<button className="font-primary font-medium text-md text-success-green" onClick={() => setIsAdding(true)}>+ Add an alias</button>
										)}
										{isAdding && (
											<div className="w-full overflow-hidden transition-all duration-300 ease-in-out border-t pt-2">
												<div className="mt-2 flex flex-col gap-3 text-left">
													<div className="font-primary font-medium text-sm text-primary">Enter an alias email address. This is the email address that you were provided by the forwarding service.</div>
													<div className="flex items-center gap-2">
														<input
															type="email"
															value={newAliasEmail}
															onChange={(e) => setNewAliasEmail(e.target.value)}
															placeholder="email@example.com"
															className="flex-1 bg-transparent border border-primary-200 rounded-lg px-3 py-2 text-primary font-regular font-primary text-sm"
														/>
														<button
															disabled={!isEmailValid(newAliasEmail) || isSubmitting}
															onClick={onAddAlias}
															className={`px-4 py-2 rounded-lg font-primary font-medium text-sm transition ease-in-out duration-300 ${(!isEmailValid(newAliasEmail) || isSubmitting) ? "bg-primary-200 text-primary-500 cursor-not-allowed" : "bg-success-green text-white hover:bg-success-green/80"}`}
														>
															{isSubmitting ? "Adding..." : "Add"}
														</button>
													</div>
												</div>
											</div>
										)}
									</div>
									<div className="flex flex-col gap-2">
										{aliases.map((alias) => {
											return (
												<div className="group flex items-center justify-between gap-4 bg-surface border transition ease-in-out duration-300 border-primary-100 rounded-xl p-3 h-[60px] w-full">
													<div className="flex-1 font-primary font-medium text-base text-primary text-left truncate">{alias}</div>
													<DeleteIcon className="fill-primary w-[16px] cursor-pointer hover:fill-brand-red transition ease-in-out duration-300" onClick={() => confirmDeleteAlias(alias)} />
												</div>
											);
										})}
									</div>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition>
		</>
	);
};

export default MailboxAliasModal;
